import React, { useState, useCallback, FC, useEffect } from 'react';
import { KeyboardDateTimePicker, KeyboardDateTimePickerProps } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { dateToIsoDateTimeFormat, isValidDate } from '../../utils/dateUtil';
import { useDateTimeFormat } from '../../hooks';

export interface DateTimePickerProps
  extends Pick<
    KeyboardDateTimePickerProps,
    | 'variant'
    | 'autoOk'
    | 'disabled'
    | 'error'
    | 'helperText'
    | 'minDate'
    | 'maxDate'
    | 'onBlur'
    | 'onError'
    | 'name'
    | 'id'
    | 'fullWidth'
    | 'placeholder'
    | 'clearable'
    | 'disablePast'
    | 'disableFuture'
    | 'style'
    | 'views'
    | 'autoFocus'
    | 'minutesStep'
    | 'initialFocusedDate'
  > {
  value: string | null;
  onChange: (string: string | null) => void;
  dataTestId?: string;
  /*! default: false. The picker does not lock page scroll while open, e.g. when embedded in an iframe. */
  disableScrollLock?: boolean;
  /*! onChange will be called only onClose */
  saveOnlyOnClose?: boolean;
}

export const DateTimePicker: FC<React.PropsWithChildren<DateTimePickerProps>> = ({
  value,
  onChange,
  variant = 'inline',
  autoOk = true,
  disabled = false,
  error,
  helperText,
  minDate,
  maxDate,
  onBlur,
  onError,
  name,
  id,
  minutesStep,
  saveOnlyOnClose,
  dataTestId = 'data-time-picker',
  disableScrollLock = false,
  fullWidth = true,
  placeholder,
  clearable,
  disablePast,
  disableFuture,
  style,
  autoFocus,
  initialFocusedDate,
  views
}) => {
  const { dateTimeFormat } = useDateTimeFormat();
  const [localValue, setLocalValue] = useState<MaterialUiPickersDate | null>(value as MaterialUiPickersDate);

  useEffect(() => {
    setLocalValue(value as MaterialUiPickersDate);
  }, [value]);

  const setDate = useCallback(
    (time: MaterialUiPickersDate | null) => {
      if (time && isValidDate(time)) {
        onChange(dateToIsoDateTimeFormat(time));
      } else if (time && isValidDate(new Date(time))) {
        onChange(dateToIsoDateTimeFormat(new Date(time)));
      } else if (clearable) {
        onChange(null);
      }
    },
    [onChange, clearable]
  );

  const onCloseHandler = useCallback(() => setDate(localValue), [localValue, setDate]);

  const onChangeHandler = useCallback(
    (time: MaterialUiPickersDate | null) => (saveOnlyOnClose ? setLocalValue(time) : setDate(time)),
    [setDate, saveOnlyOnClose]
  );
  return (
    <KeyboardDateTimePicker
      fullWidth={fullWidth}
      data-test={dataTestId}
      {...(disableScrollLock &&
        (variant === 'inline' ? { PopoverProps: { disableScrollLock } } : { DialogProps: { disableScrollLock } }))}
      placeholder={placeholder}
      views={views}
      format={`${dateTimeFormat.short} ${dateTimeFormat.time}`}
      variant={variant}
      autoOk={autoOk}
      value={value || null} // Must use null otherwise throws error SD-32501
      initialFocusedDate={initialFocusedDate}
      onClose={saveOnlyOnClose ? onCloseHandler : undefined}
      onBlurCapture={saveOnlyOnClose ? onCloseHandler : undefined}
      onChange={onChangeHandler}
      disabled={disabled}
      error={error}
      disablePast={disablePast}
      disableFuture={disableFuture}
      helperText={helperText}
      minDate={minDate}
      maxDate={maxDate}
      InputProps={{
        disableUnderline: true,
        placeholder,
        autoFocus,
        disabled,
        style
      }}
      minutesStep={minutesStep}
      onBlur={onBlur}
      onError={onError}
      name={name}
      id={id}
    />
  );
};
